import { Info } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/overlay/popover';
import { metricDefinitions, type MetricDefinition } from '@/lib/metricDefinitions';
import { cn } from '@/lib/utils';

interface MetricInfoButtonProps {
  /** Key into metricDefinitions, or provide a custom definition */
  metricKey?: string;
  /** Custom definition (overrides metricKey) */
  definition?: MetricDefinition;
  /** Extra class for the trigger button */
  className?: string;
}

/**
 * Clearly visible "?" info button that opens a popover with a metric explanation.
 * Designed to be obvious and tappable on all devices.
 */
export function MetricInfoButton({ metricKey, definition, className }: MetricInfoButtonProps) {
  const def = definition ?? (metricKey ? metricDefinitions[metricKey] : undefined);
  if (!def) return null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex items-center justify-center rounded-md',
            'w-[20px] h-[20px]',
            'border border-[hsl(170,80%,45%)]/20 bg-[hsl(170,80%,45%)]/8',
            'text-[hsl(170,80%,45%)]/60 hover:text-[hsl(170,80%,45%)] hover:bg-[hsl(170,80%,45%)]/15 hover:border-[hsl(170,80%,45%)]/35',
            'active:bg-[hsl(170,80%,45%)]/20',
            'transition-all duration-150',
            'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[hsl(170,80%,45%)]/40',
            'select-none touch-manipulation cursor-help',
            className,
          )}
          aria-label={`Info about ${def.title}`}
        >
          <Info className="h-3 w-3 pointer-events-none" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="start"
        sideOffset={6}
        collisionPadding={16}
        className={cn(
          'w-[min(18rem,calc(100vw-2rem))] p-3 rounded-lg shadow-xl z-[100]',
          'border border-white/[0.15] bg-[rgba(12,16,24,0.96)] backdrop-blur-[24px]',
        )}
      >
        <div className="space-y-1.5">
          <div className="flex items-center gap-1.5 flex-wrap">
            {def.abbreviation && (
              <span className="text-[10px] font-mono font-bold tracking-wider text-[hsl(170,80%,45%)] uppercase">
                {def.abbreviation}
              </span>
            )}
            <span className="text-xs font-medium text-foreground">
              {def.title}
            </span>
          </div>
          <p className="text-[11px] leading-relaxed text-muted-foreground">
            {def.description}
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}
